/**
 * The rows `NxSettingsList` renders („Einstellungen", I-2510): one list of
 * every setting, in sections, each row a title, what it does, a control and
 * where its value comes from. The kit only knows these shapes — the host
 * translates its declarations, resolves the edited layer's values and writes
 * what the list emits.
 */

/**
 * How a row is edited: a switch, a segmented choice, a select, a number, a
 * text, a keybinding (presets plus a recorder for a combo of one's own), an
 * `order` — a sortable list of the options — or `custom`, a host's data-bound
 * kit control in the `control` slot. An `order` carries one scalar: its
 * `value` is the option values joined by `,`, in the order shown, and a
 * reorder emits `change` with the new joined string.
 */
export type NxSettingsListControl =
  | 'switch'
  | 'choice'
  | 'select'
  | 'number'
  | 'text'
  | 'keybinding'
  | 'order'
  | 'custom';

/** A custom control keeps the row's layout, origin, reset and lock behaviour. */
export interface NxSettingsControlSlot {
  item: NxSettingsListItem;
  /** Bind to the focusable control so the row's title labels it. */
  controlId: string | undefined;
  editable: boolean;
  height: 'xs' | 's';
  /** Guarded against a disabled or locked row, including after async picking. */
  onChange: (value: unknown) => void;
}

/** One option of a `choice`, a `select`, an `order` (its value is a string without `,`) or the presets of a `keybinding`. */
export interface NxSettingsListOption {
  /** `null`: the option of no value — picking it emits a reset. */
  value: string | number | null;
  label: string;
}

/**
 * Where a row's value comes from: the edited layer itself (`own`), a layer
 * before it (`inherited`, `source` names it), nobody (`default`) — or a layer
 * before it locked the field (`locked`, read-only, `source` names who).
 */
export type NxSettingsListOrigin = 'own' | 'inherited' | 'default' | 'locked';

/** One setting. */
export interface NxSettingsListItem {
  /** Unique across the list; `change`, `reset` and `lock` name it. */
  id: string;
  label: string;
  /** What the setting does. */
  description: string;
  /** Extra search words, never shown. */
  keywords?: readonly string[];
  control: NxSettingsListControl;
  /** The options of a `choice`, a `select` or an `order`; the presets of a `keybinding`. */
  options?: readonly NxSettingsListOption[];
  /**
   * A `keybinding`'s check of a recorded combo (`'Mod+Shift+F'`): the message
   * why it may not be taken — shown under the recorder, nothing is saved — or
   * null when it may.
   */
  validate?: (combo: string) => string | null;
  /** Range and step of a `number`. */
  min?: number;
  max?: number;
  step?: number;
  /** Shown after a number (`s`, `min`). */
  unit?: string;
  /** Longest text of a `text`. */
  maxLength?: number;
  /** The value the row shows: the layer's own, else the inherited one, else the default. */
  value: unknown;
  origin: NxSettingsListOrigin;
  /** Who gives the inherited value or locked the field: a caption („Firma „BLP““). */
  source?: string;
  /** The edited layer may lock the field for every layer after it (tenant, company). */
  lockable?: boolean;
  /** The edited layer locks the field. */
  locked?: boolean;
  /** Further rows the same lock covers (their titles) — a lock is per top-level field. */
  lockAlso?: readonly string[];
  /** Cannot be changed (no permission, not loaded yet). */
  disabled?: boolean;
  /** Rarely needed: folded under „Erweitert" at the end of its section. */
  advanced?: boolean;
  /** Id of the section's group the row is listed under. */
  group?: string;
}

/** A sub-heading of a section. */
export interface NxSettingsListGroup {
  id: string;
  label: string;
}

/**
 * A part of a `custom` section for the search — what a row is to the others:
 * its title, what it is for and extra words, without a control. The host
 * renders the part itself.
 */
export interface NxSettingsListTopic {
  /** Unique within its section; the `content` slot names the parts that match. */
  id: string;
  label: string;
  description?: string;
  /** Extra search words, never shown. */
  keywords?: readonly string[];
}

/** One section: one entry of the list's navigation. */
export interface NxSettingsListSection {
  id: string;
  label: string;
  description?: string;
  groups?: readonly NxSettingsListGroup[];
  items: readonly NxSettingsListItem[];
  /**
   * The host renders the section's content itself (the list's `content`
   * slot) instead of rows — an editor that is no list of settings, like the
   * look designer's colours. Its `topics` take part in the search; it has no
   * values of its own for „Nur geänderte".
   */
  custom?: boolean;
  /** The searchable parts of a `custom` section; without any, its title and description. */
  topics?: readonly NxSettingsListTopic[];
}

/** What the search reads of a row or of a topic. */
export type NxSettingsSearchable = Pick<NxSettingsListItem, 'label'> &
  Partial<Pick<NxSettingsListItem, 'description' | 'keywords' | 'options'>>;

/** Case- and accent-insensitive form for the search („Größe“ ↔ „grosse“, „Übersicht“ ↔ „ubersicht“). */
export function nxSettingsSearchText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .toLocaleLowerCase();
}

/** The words of a query, normalized; empty for a blank one. */
export function nxSettingsSearchWords(query: string): string[] {
  return nxSettingsSearchText(query).split(/\s+/).filter(Boolean);
}

/**
 * Whether a row (or a topic of a custom section) matches every word of a
 * query: in its title, its description, its keywords, its options, its group
 * or its section.
 */
export function nxSettingsItemMatches(
  item: NxSettingsSearchable,
  words: readonly string[],
  context: { section?: string; group?: string } = {}
): boolean {
  if (!words.length) return true;
  const haystack = nxSettingsSearchText(
    [
      item.label,
      item.description ?? '',
      ...(item.keywords ?? []),
      ...(item.options ?? []).map((option) => option.label),
      context.group ?? '',
      context.section ?? '',
    ].join(' ')
  );
  return words.every((word) => haystack.includes(word));
}

/**
 * The topics of a custom section that match every word of a query, in their
 * order — the section's own title and description stand in for a section
 * that names none (its id is the hit then).
 */
export function nxSettingsTopicHits(
  section: Pick<NxSettingsListSection, 'id' | 'label' | 'description' | 'topics'>,
  words: readonly string[]
): string[] {
  const topics: readonly NxSettingsListTopic[] = section.topics?.length
    ? section.topics
    : [{ id: section.id, label: section.label, description: section.description }];
  return topics
    .filter((topic) => nxSettingsItemMatches(topic, words, { section: section.label }))
    .map((topic) => topic.id);
}

function decimalsOf(step: number): number {
  const text = String(step);
  const dot = text.indexOf('.');
  return dot < 0 ? 0 : text.length - dot - 1;
}

/**
 * A number a user typed, as a `number` row stores it: comma or point, kept
 * inside `min`/`max` and on the `step` grid (counted from `min`). `null` for
 * anything that is no number.
 */
export function nxSettingsNumber(
  raw: unknown,
  item: Pick<NxSettingsListItem, 'min' | 'max' | 'step'>
): number | null {
  const text =
    typeof raw === 'number'
      ? String(raw)
      : String(raw ?? '')
          .trim()
          .replace(',', '.');
  if (!text) return null;
  let value = Number(text);
  if (!Number.isFinite(value)) return null;
  const step = item.step && item.step > 0 ? item.step : undefined;
  if (step) {
    const base = item.min ?? 0;
    value = base + Math.round((value - base) / step) * step;
    value = Number(value.toFixed(Math.max(decimalsOf(step), decimalsOf(base))));
  }
  if (item.min != null && value < item.min) value = item.min;
  if (item.max != null && value > item.max) value = item.max;
  return value;
}
